import { cardProgramIsGatewayIssuingEnabled, isGatewayCardOrProgram } from '@Utils/gatewayCards';
import { keyBy } from 'lodash';
import { toast } from 'react-toastify';
import { AnyAction, Dispatch } from 'redux';

import { AddUserConnectedSubaccountModalConnected } from '@ReactComponents/AdminSettings/Components/CardPrograms/AddUserConnectedSubaccountModal/AddUserConnectedSubaccountModal';
import { DeleteCardProgramConfirmModal } from '@ReactComponents/AdminSettings/Components/CardPrograms/DeleteCardProgramConfirmModal';
import { RenameCardModal } from '@ReactComponents/AdminSettings/Components/CardPrograms/RenameCardModal';
import { SuspendCardProgramConfirmModal } from '@ReactComponents/AdminSettings/Components/CardPrograms/SuspendCardProgramConfirmModal';
import { ManageCardProgramModal } from '@ReactComponents/AdminSettings/Components/ManageCardProgram';
import { ManualUploadCardProgramModal } from '@ReactComponents/ManualUploadCardProgramModal';

import { AppThunkDispatch, AppThunkGetState } from '@Reducers/index';

import { LinkedAccountsApiService, ProgramCardCounts, StatementDateBody } from '@ClientApi/linkedAccounts';
import { IUserExtension } from '@ClientModels/userExtension';
import { IPaginationParams } from '@Shared/grid';
import { AccountCapabilities } from '@Shared/interfaces/CardsGatewayInterfaces';
import { ILinkedAccountModel } from '@Shared/models';

import {
  ADD_CAPABILITIES,
  ADD_CARD_PROGRAM,
  CARDS_SET_LOADING,
  CARD_PROGRAMS_SET_LOADING,
  REMOVE_CARD_PROGRAM,
  SET_CARDS,
  SET_CARDS_SEARCH_PARAMS,
  SET_CARD_PROGRAM,
  SET_CARD_PROGRAMS,
  SET_CARD_PROGRAM_CARD_COUNTS
} from './cardsActionsConstants';
import { getExpenseBucketsForUser } from './expenseBucketsWithTagsActions';
import { ShowModal } from './modalActions';

export interface ICardsSearchParams extends Partial<IPaginationParams> {
  parentLinkedAccountId?: string | string[];
  includeSharedLink?: boolean;
  includeUnexpensedTransactionCount?: boolean;
}

export class SetCardPrograms {
  readonly type = SET_CARD_PROGRAMS;
  payload: ILinkedAccountModel[];

  constructor(cardPrograms: ILinkedAccountModel[]) {
    this.payload = cardPrograms;
  }
}

export class AddCardProgram {
  readonly type = ADD_CARD_PROGRAM;
  payload: ILinkedAccountModel;

  constructor(cardProgram: ILinkedAccountModel) {
    this.payload = cardProgram;
  }
}

export class SetCardProgram {
  readonly type = SET_CARD_PROGRAM;
  payload: ILinkedAccountModel;

  constructor(cardProgram: ILinkedAccountModel) {
    this.payload = cardProgram;
  }
}

export class SetCards {
  readonly type = SET_CARDS;
  payload: {
    cards: ILinkedAccountModel[];
    total: number;
    paginationParams: IPaginationParams;
  };

  constructor(cards: ILinkedAccountModel[], total: number, paginationParams: IPaginationParams) {
    this.payload = { cards, total, paginationParams };
  }
}

export class RemoveCardProgram {
  readonly type = REMOVE_CARD_PROGRAM;
  payload: string;

  constructor(id: string) {
    this.payload = id;
  }
}

export class SetCardProgramCardCounts {
  readonly type = SET_CARD_PROGRAM_CARD_COUNTS;
  payload: ProgramCardCounts;

  constructor(counts: ProgramCardCounts) {
    this.payload = counts;
  }
}

export class AddCapabilities {
  readonly type = ADD_CAPABILITIES;
  payload: Record<string, AccountCapabilities>;

  constructor(capabilities: Record<string, AccountCapabilities>) {
    this.payload = capabilities;
  }
}

export class SetCardsSearchParams {
  readonly type = SET_CARDS_SEARCH_PARAMS;

  constructor(public payload: ICardsSearchParams) {}
}

export class SetLoading {
  readonly type = CARD_PROGRAMS_SET_LOADING;
  payload: boolean;

  constructor(loading: boolean) {
    this.payload = loading;
  }
}

export class SetCardsLoading {
  readonly type = CARDS_SET_LOADING;
  payload: boolean;

  constructor(loading: boolean) {
    this.payload = loading;
  }
}

export const init =
  (includeSubaccounts = true) =>
  async (dispatch: AppThunkDispatch) => {
    dispatch(new SetLoading(true));
    const programsPromise = LinkedAccountsApiService.getCompanyCardPrograms({
      overrideStore: true,
      onlyPending: false,
      includeStatementCycle: true,
      includeSubaccounts: includeSubaccounts
    });
    const programCardCountsPromise = LinkedAccountsApiService.getProgramCardCounts();
    const capabilitiesPromise = LinkedAccountsApiService.getGatewayProgramsCapabilities();

    const [programs, counts, capabilities] = await Promise.all([programsPromise, programCardCountsPromise, capabilitiesPromise]);

    dispatch(new AddCapabilities(keyBy(capabilities, 'programId')));
    dispatch(new SetCardPrograms(programs));
    dispatch(new SetCardProgramCardCounts(counts));
    dispatch(new SetLoading(false));
  };

type GetCardsOptions = {
  includeSharedLink?: boolean;
  includeUnexpensedTransactionCount?: boolean;
  types?: string[];
};

export const getCards =
  (
    parentLinkedAccountId: string | string[],
    { page, pageSize, sortField, sortDirection, filters, search }: IPaginationParams,
    { includeSharedLink = false, includeUnexpensedTransactionCount = false, types = null }: GetCardsOptions
  ) =>
  async (dispatch: AppThunkDispatch, getState: AppThunkGetState) => {
    dispatch(new SetCardsLoading(true));

    const defaultFilters: Record<string, any> = {
      state: ['active'],
      connected_to: null
    };
    const updatedFilters: Record<string, any> = {
      ...defaultFilters,
      ...filters
    };
    const requestSearchParams: ICardsSearchParams = {
      parentLinkedAccountId,
      page,
      pageSize,
      sortField,
      sortDirection,
      search,
      includeSharedLink,
      includeUnexpensedTransactionCount,
      filters: updatedFilters
    };
    dispatch(new SetCardsSearchParams(requestSearchParams));

    const cards = await LinkedAccountsApiService.searchCardsForProgram(
      parentLinkedAccountId,
      pageSize,
      page * pageSize,
      sortField,
      sortDirection,
      updatedFilters,
      search,
      true,
      includeSharedLink,
      includeUnexpensedTransactionCount,
      types
    );
    const { searchParams } = getState().cards;
    if (requestSearchParams !== searchParams) {
      return;
    }

    const uniqueUserIds = new Set<string>();
    cards.rows.forEach(({ user_id }) => uniqueUserIds.add(user_id));

    const expenseBucketPromises = [];

    const expenseBucketsByUser = getState().expenseBucketsWithTags.expenseBucketsByUser;

    for (const userId of uniqueUserIds) {
      if (!expenseBucketsByUser[userId]) {
        expenseBucketPromises.push(dispatch(getExpenseBucketsForUser(userId)));
      }
    }

    await Promise.all(expenseBucketPromises);

    dispatch(
      new SetCards(cards.rows, cards.count, {
        page,
        pageSize,
        sortField,
        sortDirection,
        filters: updatedFilters,
        search
      })
    );
    dispatch(new SetCardsLoading(false));
  };

export const newCard =
  (
    dispatch: Dispatch<AnyAction>,
    openIssueCardDrawer: (program: ILinkedAccountModel) => void,
    capabilities: Record<string, AccountCapabilities>
  ) =>
  async (program: ILinkedAccountModel) => {
    if (isGatewayCardOrProgram(program)) {
      const programCapabilities = capabilities[program.id];
      if (cardProgramIsGatewayIssuingEnabled(program, programCapabilities)) {
        return openIssueCardDrawer(program);
      }
    }
  };

export const suspendProgram = (dispatch: Dispatch<AnyAction>) => async (program: ILinkedAccountModel) => {
  dispatch(
    new ShowModal(SuspendCardProgramConfirmModal, {
      program,
      onSuspend: async () => {
        dispatch(new SetLoading(true));
        const updatedProgram = await LinkedAccountsApiService.suspendProgram(program.id);
        dispatch(new SetCardProgram(updatedProgram));
        dispatch(new SetLoading(false));
      }
    })
  );
};

export const deleteProgram = (dispatch: Dispatch<AnyAction>, counts: ProgramCardCounts) => async (program: ILinkedAccountModel) => {
  let cardCount = 0;
  if (counts[program.id]) {
    const { assigned, unassigned } = counts[program.id];
    cardCount = assigned + unassigned;
  }

  dispatch(
    new ShowModal(DeleteCardProgramConfirmModal, {
      program,
      cardCount,
      onDelete: async () => {
        dispatch(new SetLoading(true));
        await LinkedAccountsApiService.deleteProgram(program.id);
        dispatch(new RemoveCardProgram(program.id));
        dispatch(new SetLoading(false));
      }
    })
  );
};

export const uploadTransactions = (dispatch: Dispatch<AnyAction>) => async (program: ILinkedAccountModel) => {
  dispatch(
    new ShowModal(ManualUploadCardProgramModal, {
      cardProgram: program,
      firstPanel: 'start',
      onSuccess: () => toast.success(`Transactions uploaded.`),
      onFailure: (message?: string) => toast.error(message || 'Something went wrong; Some updates may not have applied.')
    })
  );
};

export const addCards = (dispatch: AppThunkDispatch) => async (programId: string) => {
  const setAllCards = () => dispatch(init());

  dispatch(
    new ShowModal(AddUserConnectedSubaccountModalConnected, {
      linkedAccountId: programId,
      onCardAdded: setAllCards
    })
  );
};

export const updateStatementDate = (dispatch: AppThunkDispatch) => async (programId: string, data: StatementDateBody) => {
  const updatedProgram = await LinkedAccountsApiService.updateCardProgramStatementDate(programId, data);
  dispatch(new SetCardProgram(updatedProgram));
};

export const manageCards = (dispatch: AppThunkDispatch) => (cards: ILinkedAccountModel[], users: IUserExtension[]) => {
  const setAllCards = () => dispatch(init());

  dispatch(
    new ShowModal(ManageCardProgramModal, {
      cards,
      users,
      cardsUpdated: setAllCards,
      transactionsBulkSkipped: setAllCards
    })
  );
};

export const renameCard = (dispatch: AppThunkDispatch) => (card: ILinkedAccountModel) => {
  dispatch(
    new ShowModal(RenameCardModal, {
      card,
      onEdit: async (newName: string) => {
        const updatedProgram = await LinkedAccountsApiService.updateCardProgramName(card.id, newName);
        dispatch(new SetCardProgram(updatedProgram));
      }
    })
  );
};

export type CardsAction =
  | SetCardPrograms
  | SetCardProgram
  | SetCards
  | SetCardsSearchParams
  | RemoveCardProgram
  | SetCardProgramCardCounts
  | AddCapabilities
  | SetLoading
  | SetCardsLoading
  | AddCardProgram;
